import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  approveHyperDuskPreview,
  applyHyperDuskProvenance,
  completeImageOnlyProject,
  consumeCreditsForProject,
  createHyperDuskProject,
  fetchProject,
  generateHyperDuskAnimation,
  generateHyperDuskPreview,
  pollHyperDuskStatus,
  saveEditedDuskVariant,
  saveEditedDuskVariantFromUrl,
  uploadToStorage,
} from '../services/supabase';
import { LowCreditsBanner } from '@rehub/buy-credits';
import { installPhotoEditReturnListener, openPhotoEditHandoff } from '../services/photoedit-handoff';
import ImageUploader, { type UploadedImage } from './ImageUploader';
import PolishNightModal from './PolishNightModal';
import {
  DEFAULT_HYPER_DUSK_ANIMATION_STYLE,
  DEFAULT_HYPER_DUSK_PRESET_ID,
  HYPER_DUSK_ANIMATION_STYLES,
  HYPER_DUSK_IMAGE_CREDITS,
  HYPER_DUSK_MAX_IMAGE_VARIANTS,
  HYPER_DUSK_VIDEO_CREDITS,
  HYPER_DUSK_PRESETS,
  SCENE_TYPE_OPTIONS,
  type HyperDuskAnimationStyle,
  type HyperDuskOutputMode,
  type HyperDuskProject,
  type HyperDuskProjectImage,
  type HyperDuskSceneType,
  type HyperDuskVariant,
} from '../types';
import { AIContentDisclosure } from '@rehub/ai-transparency';
import { ArrowLeft, Camera, Check, Download, ExternalLink, Film, ImageIcon, Loader2, PlayCircle, RefreshCcw, SlidersHorizontal, Sparkles } from 'lucide-react';
import { sendBackToPhotoplan, type PhotoplanReturnContext } from '../services/photoplan-return';

type AppView = 'LOGIN' | 'DASHBOARD' | 'NEW_PROJECT' | 'PROJECT_DETAIL';

interface HyperDuskProjectProps {
  userId: string;
  userCredits: number;
  onNavigate: (view: AppView, projectId?: string) => void;
  onCreditsUpdated: (newCredits: number) => void;
  onBuyCredits?: () => void;
  initialImageUrl?: string;
  onImageConsumed?: () => void;
  photoplanReturn?: PhotoplanReturnContext | null;
}

// Kling 3.0 Pro only emits 16:9, 9:16, or 1:1 — picking anything else
// (4:3, 3:4) leaves Kling free to stretch the source to fit its native
// canvas. The edge function `imagemotion-hyperdusk-poll` letterboxes the
// before/after frames into the chosen ratio, so squarish photos snap
// to 1:1 and wide/tall photos snap to 16:9 / 9:16 without squashing.
type SupportedAspectRatio = '16:9' | '9:16' | '1:1';

function detectClosestAspectRatio(width?: number, height?: number): SupportedAspectRatio {
  if (!width || !height || width <= 0 || height <= 0) return '16:9';
  const ratio = width / height;
  const candidates: Array<{ value: SupportedAspectRatio; ratio: number }> = [
    { value: '16:9', ratio: 16 / 9 },
    { value: '9:16', ratio: 9 / 16 },
    { value: '1:1', ratio: 1 },
  ];

  return candidates.reduce((best, current) => {
    const bestDelta = Math.abs(best.ratio - ratio);
    const currentDelta = Math.abs(current.ratio - ratio);
    return currentDelta < bestDelta ? current : best;
  }).value;
}

function toUiError(err: unknown, fallback: string): string {
  if (err instanceof Error && err.message) return err.message;
  if (err && typeof err === 'object') {
    const maybe = err as { message?: string; details?: string };
    if (maybe.message && maybe.details) return `${maybe.message} (${maybe.details})`;
    if (maybe.message) return maybe.message;
  }
  return fallback;
}

export default function HyperDuskProject({
  userId,
  userCredits,
  onNavigate,
  onCreditsUpdated,
  onBuyCredits,
  initialImageUrl,
  onImageConsumed,
  photoplanReturn,
}: HyperDuskProjectProps) {
  const logoSrc = '/hyperdusk-logo-wordmark-alpha.png';
  const [projectName, setProjectName] = useState('');
  const [uploadedImages, setUploadedImages] = useState<UploadedImage[]>([]);
  const [projectId, setProjectId] = useState<string | null>(null);
  const [project, setProject] = useState<HyperDuskProject | null>(null);
  const [generatedImages, setGeneratedImages] = useState<HyperDuskProjectImage[]>([]);
  const [loadingProject, setLoadingProject] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [approving, setApproving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [generatingAnimation, setGeneratingAnimation] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [outputMode, setOutputMode] = useState<HyperDuskOutputMode>('video');
  const [finishing, setFinishing] = useState(false);
  const [selectedPresetId, setSelectedPresetId] = useState(DEFAULT_HYPER_DUSK_PRESET_ID);
  const [selectedVariantId, setSelectedVariantId] = useState<string | null>(null);
  const [animationDurationSeconds, setAnimationDurationSeconds] = useState(5);
  const [animationStyle, setAnimationStyle] = useState<HyperDuskAnimationStyle>(DEFAULT_HYPER_DUSK_ANIMATION_STYLE);
  const [sceneType, setSceneType] = useState<HyperDuskSceneType>('exterior');
  const [loadingInitialImage, setLoadingInitialImage] = useState(!!initialImageUrl);
  const [showPolishModal, setShowPolishModal] = useState(false);
  const initialImageConsumed = useRef(false);
  const duskProvenanceAppliedRef = useRef<Set<string>>(new Set());

  const uploaded = uploadedImages[0];
  const canStart = !!uploaded?.url && !uploaded.uploading;
  // While creating, follow the selected mode; once the project exists, trust
  // the persisted mode so reloads / SSO re-entries stay consistent.
  const isImageMode = projectId
    ? (project?.settings?.output_mode ?? 'video') === 'image'
    : outputMode === 'image';
  const requiredCredits = isImageMode ? HYPER_DUSK_IMAGE_CREDITS : HYPER_DUSK_VIDEO_CREDITS;
  const selectedPreset = useMemo(
    () => HYPER_DUSK_PRESETS.find((preset) => preset.id === selectedPresetId) ?? HYPER_DUSK_PRESETS[0],
    [selectedPresetId]
  );
  const activeNanoPrompt = sceneType === 'interior' ? selectedPreset.nanoPromptInterior : selectedPreset.nanoPrompt;
  const activeKlingPrompt = sceneType === 'interior' ? selectedPreset.klingPromptBaseInterior : selectedPreset.klingPromptBase;
  const selectedGeneratedImage = generatedImages[0] ?? null;
  const variants = (selectedGeneratedImage?.dusk_variants ?? []) as HyperDuskVariant[];
  const selectedVariant = useMemo(() => {
    if (!variants.length) return null;
    if (selectedVariantId) {
      const found = variants.find((variant) => variant.id === selectedVariantId);
      if (found) return found;
    }
    if (selectedGeneratedImage?.selected_dusk_variant_id) {
      const found = variants.find((variant) => variant.id === selectedGeneratedImage.selected_dusk_variant_id);
      if (found) return found;
    }
    return variants[variants.length - 1];
  }, [selectedVariantId, selectedGeneratedImage?.selected_dusk_variant_id, variants]);
  const sourcePreview = selectedGeneratedImage?.source_image_url || selectedGeneratedImage?.image_url || uploaded?.preview || null;
  const duskPreview = selectedVariant?.url || selectedGeneratedImage?.dusk_image_url || null;
  // Portrait sources (9:16 / tall 3:4) need a tighter comparison layout so
  // side-by-side panes don't become unusably tall.
  const isPortraitPreview =
    project?.settings?.aspect_ratio === '9:16' ||
    (!!uploaded?.width && !!uploaded?.height && uploaded.width < uploaded.height);

  const isDuskReadyForReview = !!duskPreview && (
    selectedGeneratedImage?.generation_status === 'awaiting_user_approval' ||
    selectedGeneratedImage?.generation_status === 'dusk_ready' ||
    selectedGeneratedImage?.generation_status === 'kling_processing' ||
    selectedGeneratedImage?.generation_status === 'animation_ready' ||
    selectedGeneratedImage?.generation_status === 'completed'
  );

  const isApproved =
    selectedGeneratedImage?.generation_status === 'dusk_ready' ||
    selectedGeneratedImage?.generation_status === 'kling_processing' ||
    selectedGeneratedImage?.generation_status === 'animation_ready' ||
    selectedGeneratedImage?.generation_status === 'completed';

  const isAnimationRunning =
    project?.status === 'rendering' ||
    selectedGeneratedImage?.generation_status === 'kling_processing';

  const isCompleted = project?.status === 'completed';
  const canGenerateAnimation = isApproved && !!duskPreview && !generatingAnimation && !isAnimationRunning;
  const variantLimitReached = isImageMode && variants.length >= HYPER_DUSK_MAX_IMAGE_VARIANTS;

  const stepLabel = useMemo(() => {
    if (isImageMode) {
      if (!projectId) return 'Step 1 of 3: Upload Image';
      if (!isDuskReadyForReview) return 'Step 2 of 3: Generating Evening Image';
      return 'Step 3 of 3: Review & Save';
    }
    if (!projectId) return 'Step 1 of 4: Upload Image';
    if (!isDuskReadyForReview) return 'Step 2 of 4: Generating Night Version';
    if (!isApproved) return 'Step 3 of 4: Review and Approve Night Version';
    if (!isCompleted) return 'Step 4 of 4: Generate Animation (Duration + Motion)';
    return 'Completed';
  }, [isImageMode, projectId, isDuskReadyForReview, isApproved, isCompleted]);

  const loadProject = useCallback(async () => {
    if (!projectId) return;
    setLoadingProject(true);
    const data = await fetchProject(projectId);
    setProject(data.project);
    setGeneratedImages(data.images);
    setLoadingProject(false);
  }, [projectId]);

  useEffect(() => {
    if (!projectId) return;
    loadProject();
  }, [projectId, loadProject]);

  // Accept PhotoEdit round-trip results while this workflow is open.
  useEffect(() => {
    if (!projectId || !userId) return;
    return installPhotoEditReturnListener((payload) => {
      if (payload.projectId !== projectId) return;
      void (async () => {
        try {
          const { variantId } = await saveEditedDuskVariantFromUrl({
            projectId: payload.projectId,
            imageId: payload.imageId,
            userId,
            imageUrl: payload.imageUrl,
          });
          setSelectedVariantId(variantId);
          await loadProject();
        } catch (err) {
          setError(toUiError(err, 'Failed to import edited image from PhotoEdit'));
        }
      })();
    });
  }, [projectId, userId, loadProject]);

  const hasProcessingImages = generatedImages.some(
    (img) => img.generation_status === 'kling_processing'
  );
  // Drive the poll worker for projects whose Kling clip finished but
  // were never finalized (legacy projects stuck at `animation_ready`
  // because the old imagemotion-render trigger timed out).
  const hasPendingFinalization =
    !!project &&
    project.status !== 'completed' &&
    !project.final_video_url &&
    generatedImages.some((img) => img.generation_status === 'animation_ready');

  useEffect(() => {
    if (!projectId) return;
    if (!project) return;
    const shouldPoll =
      project.status === 'queued' ||
      project.status === 'generating_clips' ||
      project.status === 'editing' ||
      project.status === 'rendering';
    if (!shouldPoll && !hasProcessingImages && !hasPendingFinalization) return;
    const timer = setInterval(async () => {
      if (hasProcessingImages || hasPendingFinalization) {
        try {
          const pollResult = await pollHyperDuskStatus(projectId);
          if (pollResult.error) {
            console.warn('Hyper Dusk poll error:', pollResult.error);
          }
          const failedResults = pollResult.results?.filter((r) => !!r.error);
          if (failedResults?.length) {
            setError(failedResults.map((r) => r.error).join('; '));
          }
        } catch (pollErr) {
          console.warn('Hyper Dusk poll call failed:', pollErr);
        }
      }
      await loadProject();
    }, 5000);
    return () => clearInterval(timer);
  }, [projectId, project?.status, project?.final_video_url, hasProcessingImages, hasPendingFinalization, loadProject]);

  useEffect(() => {
    if (isCompleted && projectId) {
      onNavigate('PROJECT_DETAIL', projectId);
    }
  }, [isCompleted, onNavigate, projectId]);

  useEffect(() => {
    if (!selectedGeneratedImage) return;
    setAnimationDurationSeconds(
      Math.max(5, Math.min(10, Math.round(selectedGeneratedImage.clip_duration || 5)))
    );
    if (selectedGeneratedImage.kling_animation_style) {
      setAnimationStyle(selectedGeneratedImage.kling_animation_style);
    }
  }, [selectedGeneratedImage?.id]);

  useEffect(() => {
    if (!variants.length) {
      setSelectedVariantId(null);
      return;
    }
    const preferredId = selectedGeneratedImage?.selected_dusk_variant_id || variants[variants.length - 1].id;
    setSelectedVariantId((current) => current ?? preferredId);
  }, [variants, selectedGeneratedImage?.selected_dusk_variant_id]);

  // Persist dusk provenance once when the still becomes ready for review.
  useEffect(() => {
    if (!selectedGeneratedImage?.id || !isDuskReadyForReview) return;
    if (duskProvenanceAppliedRef.current.has(selectedGeneratedImage.id)) return;
    duskProvenanceAppliedRef.current.add(selectedGeneratedImage.id);
    void applyHyperDuskProvenance(selectedGeneratedImage.id, {
      sourceAssetId:
        selectedGeneratedImage.source_image_path ||
        selectedGeneratedImage.source_image_url ||
        selectedGeneratedImage.image_url,
      outputAssetId:
        selectedVariant?.path ||
        selectedGeneratedImage.dusk_image_path ||
        selectedGeneratedImage.dusk_image_url,
      operation: 'hyperdusk_dusk',
    });
  }, [
    selectedGeneratedImage?.id,
    isDuskReadyForReview,
    selectedGeneratedImage?.source_image_path,
    selectedGeneratedImage?.source_image_url,
    selectedGeneratedImage?.image_url,
    selectedGeneratedImage?.dusk_image_path,
    selectedGeneratedImage?.dusk_image_url,
    selectedVariant?.path,
  ]);

  useEffect(() => {
    if (!initialImageUrl || initialImageConsumed.current) return;
    initialImageConsumed.current = true;
    onImageConsumed?.();

    (async () => {
      setLoadingInitialImage(true);
      try {
        const response = await fetch(initialImageUrl);
        if (!response.ok) throw new Error(`Failed to fetch image: ${response.status}`);
        const blob = await response.blob();
        const ext = blob.type.includes('png') ? '.png' : '.jpg';
        const rawFile = new File([blob], `imported-image${ext}`, { type: blob.type || 'image/jpeg' });

        const MAX_DIM = 1920;
        const { file, width, height } = await new Promise<{ file: File; width: number; height: number }>((resolve) => {
          const img = new Image();
          img.onload = () => {
            let w = img.naturalWidth;
            let h = img.naturalHeight;
            if (w > MAX_DIM || h > MAX_DIM) {
              const ratio = Math.min(MAX_DIM / w, MAX_DIM / h);
              w = Math.round(w * ratio);
              h = Math.round(h * ratio);
            }
            const canvas = document.createElement('canvas');
            canvas.width = w;
            canvas.height = h;
            const ctx = canvas.getContext('2d');
            if (!ctx) { resolve({ file: rawFile, width: w, height: h }); return; }
            ctx.drawImage(img, 0, 0, w, h);
            canvas.toBlob(
              (b) => {
                if (!b) { resolve({ file: rawFile, width: w, height: h }); return; }
                resolve({ file: new File([b], 'imported-image.jpg', { type: 'image/jpeg' }), width: w, height: h });
              },
              'image/jpeg',
              0.85
            );
          };
          img.onerror = () => resolve({ file: rawFile, width: 0, height: 0 });
          img.src = URL.createObjectURL(blob);
        });

        const tempId = crypto.randomUUID();
        const preview = URL.createObjectURL(file);
        setUploadedImages([{ id: tempId, preview, url: null, path: null, uploading: true }]);

        const result = await uploadToStorage(file, 'images', userId);
        setUploadedImages([{
          id: tempId,
          preview,
          url: result.url,
          path: result.path,
          uploading: false,
          width,
          height,
        }]);
      } catch (err) {
        console.error('Failed to auto-load incoming image:', err);
        setError('Failed to import image. Please upload manually.');
        setUploadedImages([]);
      } finally {
        setLoadingInitialImage(false);
      }
    })();
  }, [initialImageUrl, userId, onImageConsumed]);

  const startHyperDusk = async () => {
    if (!uploaded?.url || uploaded.uploading) return;
    const productLabel = isImageMode ? 'Evening Images' : 'Hyper Dusk';
    if (userCredits < requiredCredits) {
      setError(`Not enough credits. You need ${requiredCredits} credits for ${productLabel}.`);
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const detectedAspectRatio = detectClosestAspectRatio(uploaded.width, uploaded.height);

      const newProjectId = await createHyperDuskProject({
        userId,
        name: projectName.trim() || (isImageMode ? 'Evening Image Project' : 'Hyper Dusk Project'),
        image: { url: uploaded.url, path: uploaded.path ?? null },
        nanoPrompt: activeNanoPrompt,
        klingPromptBase: activeKlingPrompt,
        animationStyle,
        animationDurationSeconds,
        aspectRatio: detectedAspectRatio,
        outputMode,
      });

      const creditResult = await consumeCreditsForProject(userId, newProjectId, requiredCredits);
      if (!creditResult.success) {
        setError(
          creditResult.error === 'INSUFFICIENT_CREDITS'
            ? `Not enough credits. You need ${requiredCredits} credits for ${productLabel}.`
            : 'Failed to process credits. Please try again.'
        );
        setSubmitting(false);
        return;
      }

      if (creditResult.credits_remaining !== undefined) {
        onCreditsUpdated(creditResult.credits_remaining);
      }

      setProjectId(newProjectId);
      await generateHyperDuskPreview(newProjectId, {
        presetId: selectedPreset.id,
        nanoPrompt: activeNanoPrompt,
        klingPromptBase: activeKlingPrompt,
        animationStyle,
        sceneType,
      });
      await loadProject();
    } catch (err) {
      setError(toUiError(err, 'Failed to start Hyper Dusk project'));
    } finally {
      setSubmitting(false);
    }
  };

  const regenerateNightVersion = async () => {
    if (!projectId) return;
    setRegenerating(true);
    setError(null);
    try {
      await generateHyperDuskPreview(projectId, {
        presetId: selectedPreset.id,
        nanoPrompt: activeNanoPrompt,
        klingPromptBase: activeKlingPrompt,
        animationStyle,
        sceneType,
      });
      await loadProject();
    } catch (err) {
      setError(toUiError(err, 'Failed to regenerate night version'));
    } finally {
      setRegenerating(false);
    }
  };

  const approveNightVersion = async () => {
    if (!projectId || !selectedGeneratedImage?.id || !duskPreview) return;
    setApproving(true);
    setError(null);
    try {
      await approveHyperDuskPreview(projectId, selectedGeneratedImage.id, selectedVariant?.id);
      await loadProject();
    } catch (err) {
      setError(toUiError(err, 'Failed to approve night version'));
    } finally {
      setApproving(false);
    }
  };

  const handleSavePolish = async (file: File) => {
    if (!projectId || !selectedGeneratedImage?.id) return;
    const { variantId } = await saveEditedDuskVariant({
      projectId,
      imageId: selectedGeneratedImage.id,
      userId,
      file,
    });
    setSelectedVariantId(variantId);
    await loadProject();
  };

  const openInPhotoEdit = () => {
    if (!projectId || !selectedGeneratedImage?.id || !duskPreview) return;
    openPhotoEditHandoff({
      imageUrl: duskPreview,
      projectId,
      imageId: selectedGeneratedImage.id,
    });
  };

  const runAnimation = async () => {
    if (!projectId || !duskPreview) return;
    setGeneratingAnimation(true);
    setError(null);
    try {
      await generateHyperDuskAnimation(projectId, {
        durationSeconds: animationDurationSeconds,
        animationStyle,
        selectedVariantId: selectedVariant?.id,
      });
      await loadProject();
    } catch (err) {
      setError(toUiError(err, 'Failed to generate animation'));
    } finally {
      setGeneratingAnimation(false);
    }
  };

  const handleDownload = async (url: string, filename: string) => {
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`Download failed (${res.status})`);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      setError(toUiError(err, 'Download failed'));
    }
  };

  const finishImageProject = async () => {
    if (!projectId) return;
    setFinishing(true);
    setError(null);
    try {
      await completeImageOnlyProject(projectId, selectedGeneratedImage?.id);
      onNavigate('PROJECT_DETAIL', projectId);
    } catch (err) {
      setError(toUiError(err, 'Failed to save evening image'));
      setFinishing(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto animate-fade-in space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <img src={logoSrc} alt="Hyper Dusk" className="h-7 w-auto mb-2" />
          <p className="text-xs text-indigo-300 uppercase tracking-widest font-semibold">Hyper Dusk</p>
          <h1 className="text-2xl font-bold text-white">
            {isImageMode ? 'Evening Image Workflow' : 'Night Conversion Workflow'}
          </h1>
          <p className="text-sm text-slate-400 mt-1">{stepLabel}</p>
        </div>
        <button
          type="button"
          onClick={() => onNavigate('DASHBOARD')}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-300 bg-slate-900 border border-slate-700 rounded-lg hover:bg-slate-800 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
      </div>

      {error && (
        <div className="p-4 rounded-lg bg-red-900/20 border border-red-900/50 text-red-300 text-sm">
          {error}
        </div>
      )}

      {!projectId && (
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-6 space-y-5">
          <div className="flex items-center gap-3 text-indigo-300">
            <img src={logoSrc} alt="Hyper Dusk" className="h-6 w-auto" />
            <h2 className="text-lg font-semibold text-white">Create Hyper Dusk Project</h2>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">What do you want to create?</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {([
                {
                  value: 'image' as HyperDuskOutputMode,
                  icon: ImageIcon,
                  label: 'Evening Images',
                  description: 'Sunset / dusk stills only. Up to 3 variants.',
                  credits: HYPER_DUSK_IMAGE_CREDITS,
                },
                {
                  value: 'video' as HyperDuskOutputMode,
                  icon: Film,
                  label: 'Full Hyper Dusk Video',
                  description: 'Evening image + cinematic day-to-night animation.',
                  credits: HYPER_DUSK_VIDEO_CREDITS,
                },
              ]).map((option) => {
                const isSelected = option.value === outputMode;
                const OptionIcon = option.icon;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setOutputMode(option.value)}
                    className={`p-4 rounded-lg border text-left transition-colors ${
                      isSelected
                        ? 'border-indigo-500 bg-indigo-900/30'
                        : 'border-slate-700 bg-slate-800/40 hover:border-slate-600'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <OptionIcon className="w-4 h-4 text-indigo-300" />
                        <p className="text-sm font-semibold text-white">{option.label}</p>
                      </div>
                      <span className="text-xs font-medium text-indigo-200 bg-indigo-900/40 px-2 py-0.5 rounded-full">
                        {option.credits} credits
                      </span>
                    </div>
                    <p className="text-xs text-slate-400 mt-2">{option.description}</p>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Project Name</label>
            <input
              type="text"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              placeholder="e.g. Marbella Pool Villa"
              className="w-full px-4 py-2 border border-slate-700 rounded-lg bg-slate-800 text-white placeholder-slate-500 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              maxLength={120}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              {loadingInitialImage ? 'Importing image...' : 'Upload One Image'}
            </label>
            {loadingInitialImage ? (
              <div className="flex items-center justify-center py-12 border-2 border-dashed border-slate-700 rounded-lg bg-slate-800/50">
                <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
                <span className="ml-3 text-slate-400 text-sm">Loading image from Photoclear / Staging...</span>
              </div>
            ) : (
              <ImageUploader
                images={uploadedImages}
                onChange={setUploadedImages}
                userId={userId}
                maxImages={1}
                onUploadError={(message) => setError(message)}
              />
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Image Type</label>
            <p className="text-xs text-slate-500 mb-3">Select whether this is an exterior or interior photo so the AI applies the correct lighting.</p>
            <div className="grid grid-cols-2 gap-3">
              {SCENE_TYPE_OPTIONS.map((option) => {
                const isSelected = option.value === sceneType;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setSceneType(option.value)}
                    className={`p-3 rounded-lg border text-left transition-colors ${
                      isSelected
                        ? 'border-indigo-500 bg-indigo-900/30'
                        : 'border-slate-700 bg-slate-800/40 hover:border-slate-600'
                    }`}
                  >
                    <p className="text-sm font-semibold text-white">{option.label}</p>
                    <p className="text-xs text-slate-400 mt-1">{option.description}</p>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-3">Dusk Style Preset</label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {HYPER_DUSK_PRESETS.map((preset) => {
                const isSelected = preset.id === selectedPresetId;
                return (
                  <button
                    key={preset.id}
                    type="button"
                    onClick={() => {
                      setSelectedPresetId(preset.id);
                      setAnimationStyle(preset.defaultAnimationStyle);
                    }}
                    className={`p-3 rounded-lg border text-left transition-colors ${
                      isSelected
                        ? 'border-indigo-500 bg-indigo-900/30'
                        : 'border-slate-700 bg-slate-800/40 hover:border-slate-600'
                    }`}
                  >
                    <p className="text-sm font-semibold text-white">{preset.label}</p>
                    <p className="text-xs text-slate-400 mt-1">{preset.description}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {onBuyCredits && userCredits < requiredCredits && (
            <LowCreditsBanner balance={userCredits} needed={requiredCredits} onBuy={onBuyCredits} />
          )}
          <div className="flex items-center justify-between pt-2 border-t border-slate-800">
            <div>
              <p className="text-sm text-slate-300">Cost: {requiredCredits} credits</p>
              <p className="text-xs text-slate-500">Balance: {userCredits} credits</p>
            </div>
            <button
              type="button"
              onClick={startHyperDusk}
              disabled={!canStart || submitting || userCredits < requiredCredits}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {submitting
                ? 'Starting...'
                : isImageMode
                ? `Generate — ${HYPER_DUSK_IMAGE_CREDITS} credit`
                : `Generate Night Preview — ${HYPER_DUSK_VIDEO_CREDITS} credits`}
            </button>
          </div>
        </div>
      )}

      {projectId && (
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-6 space-y-5">
          {!isDuskReadyForReview && (
            <div className="flex items-center gap-2 text-amber-300">
              <Loader2 className="w-5 h-5 animate-spin" />
              {isImageMode ? 'Generating evening image...' : 'Generating dusk preview...'}
            </div>
          )}

          <div
            className={`grid gap-4 ${
              isPortraitPreview
                ? 'grid-cols-1 sm:grid-cols-2 max-w-3xl mx-auto'
                : 'grid-cols-1 lg:grid-cols-2'
            }`}
          >
            <div className="rounded-lg border border-slate-700 bg-slate-950 p-3">
              <p className="text-xs uppercase tracking-wide text-slate-400 mb-2">Original</p>
              <div className="rounded bg-slate-900 flex items-center justify-center overflow-hidden min-h-[12rem]">
                {sourcePreview ? (
                  <img
                    src={sourcePreview}
                    alt="Original upload"
                    className="max-h-[70vh] w-full object-contain"
                  />
                ) : (
                  <span className="text-xs text-slate-500 py-16">Waiting for source image</span>
                )}
              </div>
            </div>
            <div className="rounded-lg border border-slate-700 bg-slate-950 p-3">
              <div className="flex items-center justify-between mb-2 gap-2">
                <p className="text-xs uppercase tracking-wide text-slate-400">{isImageMode ? 'Evening Image' : 'Night Version'}</p>
                {duskPreview && (
                  <AIContentDisclosure
                    classification="AI_MODIFIED"
                    operation="hyperdusk_dusk"
                    app="hyperdusk"
                  />
                )}
              </div>
              <div className="rounded bg-slate-900 flex items-center justify-center overflow-hidden min-h-[12rem]">
                {duskPreview ? (
                  <img
                    src={duskPreview}
                    alt="Generated night preview"
                    className="max-h-[70vh] w-full object-contain"
                  />
                ) : (
                  <span className="text-xs text-slate-500 py-16">Generating preview...</span>
                )}
              </div>
            </div>
          </div>

          {variants.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-xs uppercase tracking-wide text-slate-400">Saved Variants ({variants.length})</p>
                <p className="text-xs text-slate-500">Select one version to approve/animate</p>
              </div>
              <div
                className={`grid gap-3 ${
                  isPortraitPreview
                    ? 'grid-cols-3 sm:grid-cols-4 lg:grid-cols-6'
                    : 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4'
                }`}
              >
                {variants.map((variant, index) => {
                  const isSelected = variant.id === selectedVariant?.id;
                  return (
                    <button
                      key={variant.id}
                      type="button"
                      onClick={() => setSelectedVariantId(variant.id)}
                      className={`rounded-lg border p-2 text-left transition-colors ${
                        isSelected
                          ? 'border-indigo-500 bg-indigo-900/20'
                          : 'border-slate-700 bg-slate-800/40 hover:border-slate-600'
                      }`}
                    >
                      <div
                        className={`rounded overflow-hidden bg-slate-900 flex items-center justify-center w-full ${
                          isPortraitPreview ? 'aspect-[9/16] max-h-48 mx-auto' : ''
                        }`}
                      >
                        {variant.url ? (
                          <img
                            src={variant.url}
                            alt={`Variant ${index + 1}`}
                            className={
                              isPortraitPreview
                                ? 'max-h-full max-w-full object-contain'
                                : 'max-h-40 w-full object-contain'
                            }
                          />
                        ) : (
                          <span className="text-[10px] text-slate-500 py-8">No preview</span>
                        )}
                      </div>
                      <p className="text-xs text-slate-300 mt-2 font-medium">Variant {index + 1}</p>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {isImageMode && isDuskReadyForReview && (
            <div className="space-y-3 border-t border-slate-800 pt-4">
              <p className="text-xs text-slate-400">
                {variantLimitReached
                  ? `Variant limit reached (${variants.length} of ${HYPER_DUSK_MAX_IMAGE_VARIANTS}). Download the version you like best, then save.`
                  : `You can generate up to ${HYPER_DUSK_MAX_IMAGE_VARIANTS} variants (${variants.length} of ${HYPER_DUSK_MAX_IMAGE_VARIANTS} used). Download the version you like best, then save.`}
              </p>
              <div className="flex flex-wrap items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={regenerateNightVersion}
                  disabled={regenerating || finishing || variantLimitReached}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-200 bg-slate-800 rounded-lg hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {regenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />}
                  Regenerate
                </button>
                <button
                  type="button"
                  onClick={() => duskPreview && handleDownload(duskPreview, `${projectName.trim() || 'evening-image'}.png`)}
                  disabled={!duskPreview || regenerating}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-200 bg-slate-800 rounded-lg hover:bg-slate-700 disabled:opacity-50 transition-colors"
                >
                  <Download className="w-4 h-4" />
                  Download
                </button>
                {duskPreview && (
                  <button
                    type="button"
                    onClick={openInPhotoEdit}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-200 bg-slate-800 rounded-lg hover:bg-slate-700 transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" />
                    PhotoEdit
                  </button>
                )}
                {duskPreview && (
                  <button
                    type="button"
                    onClick={() => {
                      const params = new URLSearchParams({ imageUrl: duskPreview, source: 'hyperdusk' })
                      if (photoplanReturn) {
                        params.set('returnTo', 'photoplan')
                        params.set('returnOrigin', photoplanReturn.returnOrigin)
                        params.set('bookingId', photoplanReturn.bookingId)
                        params.set('assetId', photoplanReturn.assetId)
                      }
                      window.open(`https://photoclear.rehub.studio/?${params.toString()}`, '_blank', 'noopener,noreferrer')
                    }}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-200 bg-slate-800 rounded-lg hover:bg-slate-700 transition-colors"
                  >
                    <Sparkles className="w-4 h-4" />
                    Photoclear
                  </button>
                )}
                {photoplanReturn && duskPreview && (
                  <button
                    type="button"
                    onClick={() => sendBackToPhotoplan(photoplanReturn, duskPreview)}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-sky-600 rounded-lg hover:bg-sky-500 transition-colors"
                  >
                    <Camera className="w-4 h-4" />
                    {photoplanReturn.returnPath ? 'Save to Photoplan gallery' : 'Send to Photoplan'}
                  </button>
                )}
                <button
                  type="button"
                  onClick={finishImageProject}
                  disabled={finishing || regenerating || !duskPreview}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                >
                  {finishing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  {finishing ? 'Saving...' : 'Save Evening Image'}
                </button>
              </div>
            </div>
          )}

          {!isImageMode && isDuskReadyForReview && !isApproved && (
            <div className="flex flex-wrap items-center justify-end gap-3 border-t border-slate-800 pt-4">
              <button
                type="button"
                onClick={() => setShowPolishModal(true)}
                disabled={!duskPreview || regenerating || approving}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-200 bg-slate-800 rounded-lg hover:bg-slate-700 disabled:opacity-50 transition-colors"
              >
                <SlidersHorizontal className="w-4 h-4" />
                Polish
              </button>
              <button
                type="button"
                onClick={openInPhotoEdit}
                disabled={!duskPreview || regenerating || approving}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-200 bg-slate-800 rounded-lg hover:bg-slate-700 disabled:opacity-50 transition-colors"
              >
                <ExternalLink className="w-4 h-4" />
                Open in PhotoEdit
              </button>
              <button
                type="button"
                onClick={regenerateNightVersion}
                disabled={regenerating || generatingAnimation}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-200 bg-slate-800 rounded-lg hover:bg-slate-700 disabled:opacity-50 transition-colors"
              >
                {regenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />}
                Regenerate Night Version
              </button>
              <button
                type="button"
                onClick={approveNightVersion}
                disabled={approving || regenerating || !duskPreview}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
              >
                {approving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                Approve Night Version
              </button>
              {photoplanReturn && duskPreview && (
                <button
                  type="button"
                  onClick={() => sendBackToPhotoplan(photoplanReturn, duskPreview)}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-sky-600 rounded-lg hover:bg-sky-500 transition-colors"
                >
                  <Camera className="w-4 h-4" />
                  {photoplanReturn.returnPath ? 'Save to Photoplan gallery' : 'Send to Photoplan'}
                </button>
              )}
            </div>
          )}

          {!isImageMode && isDuskReadyForReview && isApproved && (
            <div className="space-y-4 border-t border-slate-800 pt-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-400">Animation Settings</p>
                  <p className="text-xs text-slate-500 mt-1">
                    Polish the night still if needed, then pick duration and camera motion for Kling.
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setShowPolishModal(true)}
                    disabled={!duskPreview || generatingAnimation || isAnimationRunning}
                    className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-slate-200 bg-slate-800 rounded-lg hover:bg-slate-700 disabled:opacity-50 transition-colors"
                  >
                    <SlidersHorizontal className="w-3.5 h-3.5" />
                    Polish
                  </button>
                  <button
                    type="button"
                    onClick={openInPhotoEdit}
                    disabled={!duskPreview || generatingAnimation || isAnimationRunning}
                    className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-slate-200 bg-slate-800 rounded-lg hover:bg-slate-700 disabled:opacity-50 transition-colors"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    PhotoEdit
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-[auto,1fr] gap-4 items-start">
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-2">Duration</label>
                  <div className="flex flex-wrap gap-2">
                    {[5, 6, 7, 8, 9, 10].map((duration) => {
                      const isSelected = duration === animationDurationSeconds;
                      return (
                        <button
                          key={duration}
                          type="button"
                          onClick={() => setAnimationDurationSeconds(duration)}
                          disabled={generatingAnimation || isAnimationRunning}
                          className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                            isSelected
                              ? 'border-indigo-500 bg-indigo-900/40 text-white'
                              : 'border-slate-700 bg-slate-800/40 text-slate-300 hover:border-slate-600'
                          } disabled:opacity-50 disabled:cursor-not-allowed`}
                        >
                          {duration}s
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-2">Camera Motion</label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {HYPER_DUSK_ANIMATION_STYLES.map((style) => {
                      const isSelected = style.value === animationStyle;
                      return (
                        <button
                          key={style.value}
                          type="button"
                          onClick={() => setAnimationStyle(style.value)}
                          disabled={generatingAnimation || isAnimationRunning}
                          className={`p-2.5 rounded-lg border text-left transition-colors ${
                            isSelected
                              ? 'border-indigo-500 bg-indigo-900/30'
                              : 'border-slate-700 bg-slate-800/40 hover:border-slate-600'
                          } disabled:opacity-50 disabled:cursor-not-allowed`}
                        >
                          <p className="text-xs font-semibold text-white">{style.label}</p>
                          <p className="text-[11px] text-slate-400 mt-0.5 leading-snug">{style.description}</p>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
                <div className="text-xs text-slate-400">
                  Will render a <span className="font-semibold text-slate-200">{animationDurationSeconds}s</span> clip with
                  {' '}<span className="font-semibold text-slate-200">
                    {HYPER_DUSK_ANIMATION_STYLES.find((s) => s.value === animationStyle)?.label ?? animationStyle}
                  </span>.
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={regenerateNightVersion}
                    disabled={regenerating || generatingAnimation || isAnimationRunning}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-200 bg-slate-800 rounded-lg hover:bg-slate-700 disabled:opacity-50 transition-colors"
                  >
                    {regenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />}
                    Regenerate Night Version
                  </button>
                  <button
                    type="button"
                    onClick={runAnimation}
                    disabled={!canGenerateAnimation || loadingProject}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                  >
                    {generatingAnimation || isAnimationRunning ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <PlayCircle className="w-4 h-4" />
                    )}
                    {isAnimationRunning ? 'Generating Animation...' : `Generate ${animationDurationSeconds}s Animation`}
                  </button>
                </div>
              </div>
            </div>
          )}

        </div>
      )}

      {showPolishModal && duskPreview && (
        <PolishNightModal
          imageUrl={duskPreview}
          onClose={() => setShowPolishModal(false)}
          onSave={handleSavePolish}
        />
      )}
    </div>
  );
}
